import {
	SiblingNav,
	type SiblingAnchor,
} from '#app/components/sibling-nav.tsx'
import { cn } from '#app/utils/misc.tsx'

type Props = {
	children: React.ReactNode
	/** Optional in-page anchors for the sibling rail (long info pages). */
	anchors?: SiblingAnchor[]
	className?: string
}

/**
 * Two-column shell for pages inside a section.
 *
 * - Desktop: 12-col grid. Content on cols 1–9, sticky `SiblingNav` on cols
 *   10–12. The sidebar scrolls with the page but pins below the header.
 * - Mobile: content first, then `SiblingNav` rendered at the bottom (above
 *   the footer) in a muted band.
 *
 * The hero (and any full-bleed sections) live OUTSIDE this layout so they
 * can keep using their own container. SectionLayout wraps the page body —
 * the content that would have been in a `<div className="container ...">`.
 */
export function SectionLayout({ children, anchors, className }: Props) {
	return (
		<>
			<div className={cn('container py-10 md:py-14', className)}>
				<div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
					<div className="lg:col-span-9">{children}</div>
					<aside className="hidden lg:col-span-3 lg:block">
						<div className="sticky top-6">
							<SiblingNav anchors={anchors} />
						</div>
					</aside>
				</div>
			</div>

			{/* Mobile sibling rail: at the bottom of the content, before the footer. */}
			<aside className="bg-muted/40 border-border block border-t lg:hidden">
				<div className="container py-10">
					<SiblingNav anchors={anchors} />
				</div>
			</aside>
		</>
	)
}
